/* /forgot-password — request a reset link. The answer is the same whether or not the address exists. */
import "../account/account.css";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useForgotPassword } from "../../data/hooks";
import { ApiError } from "../../data/provider";
import { useI18n } from "../../i18n";
import { Alert, Button, Icon, TextField, errMsg } from "../../ui";
import { AuthSide, AuthTop } from "./components/AuthLayout";

export default function ForgotPassword() {
  const { L, t } = useI18n();
  const [sp] = useSearchParams();
  const m = useForgotPassword();
  const [email, setEmail] = useState(sp.get("email") ?? "");
  const [fe, setFe] = useState<string | undefined>();
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    setErr(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setFe(L("Enter a valid email address", "أدخل بريدًا إلكترونيًا صحيحًا")); return; }
    setFe(undefined);
    try { await m.mutateAsync(email.trim()); setDone(true); }
    catch (e) { setErr(e instanceof ApiError && e.status === 429 ? L("Too many requests — please wait a few minutes and try again.", "طلبات كثيرة — انتظر بضع دقائق ثم حاول مجددًا.") : errMsg(e)); }
  };

  return (
    <div className="auth" data-testid="forgot-password">
      <AuthSide title={L("Locked out? It happens.", "نسيت كلمة المرور؟ يحدث ذلك.")} text={L("We'll email you a secure link to choose a new password. It works once and expires in 60 minutes.", "سنرسل لك رابطًا آمنًا لاختيار كلمة مرور جديدة، يعمل مرة واحدة وتنتهي صلاحيته خلال 60 دقيقة.")} />
      <main className="auth-main no-tabbar">
        <AuthTop />
        <div className="auth-card">
          {done ? (
            <div className="stack" data-testid="forgot-done">
              <div className="inbox-hero">
                <span className="ico"><Icon name="mail" /></span>
                <h1>{L("Check your inbox", "تحقق من بريدك")}</h1>
                <p className="muted" style={{ margin: 0 }}>{L("If an account exists for ", "إذا كان هناك حساب مسجل بالبريد ")}<b dir="ltr">{email.trim()}</b>{L(", you'll receive a reset link in a minute.", " فستصلك رسالة بها رابط إعادة التعيين خلال دقيقة.")}</p>
              </div>
              <Alert tone="info" tint title={L("The link expires in 60 minutes", "تنتهي صلاحية الرابط خلال 60 دقيقة")} desc={L("Requesting a new link cancels the previous one.", "طلب رابط جديد يلغي الرابط السابق.")} />
              <div className="row" style={{ justifyContent: "center" }}>
                <Button variant="ghost" onClick={() => setDone(false)} data-testid="forgot-again">{L("Send again", "إرسال مرة أخرى")}</Button>
                <Link className="btn pri" to="/login"><span>{t("Sign in")}</span></Link>
              </div>
            </div>
          ) : (
            <form className="stack" onSubmit={submit} noValidate>
              <div>
                <h1 style={{ fontSize: 26 }}>{L("Forgot your password?", "نسيت كلمة المرور؟")}</h1>
                <p className="muted" style={{ margin: "6px 0 0" }}>{L("Enter your work email and we'll send you a reset link.", "أدخل بريد العمل وسنرسل لك رابط إعادة التعيين.")}</p>
              </div>
              {err && <Alert tone="bad" title={err} testId="forgot-error" />}
              <TextField label={L("Work email", "بريد العمل")} value={email} onChange={setEmail} type="email" dir="ltr" err={fe} testId="forgot-email" />
              <Button type="submit" variant="pri" className="block" loading={m.isPending} data-testid="forgot-submit">{L("Send reset link", "إرسال رابط إعادة التعيين")}</Button>
              <p className="sm muted" style={{ textAlign: "center", margin: 0 }}><Link to="/login">{L("Back to sign in", "العودة لتسجيل الدخول")}</Link></p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
